/**
 * React Query hooks for managing user messages (push notifications and SMS).
 * 
 * Architecture:
 * - useGetUserMessages: Fetch messages with infinite scroll pagination
 * - useGetUserMessageStatus: Lightweight unread status check (O(1) query)
 * - useMarkMessageAsRead: Mark individual message as read
 * - useMarkAllMessagesAsRead: Mark all messages as read
 * 
 * Cache Strategy:
 * - Messages use 'enabled' flag for on-demand fetching (only when modal opens)
 * - Status endpoint uses staleTime of 2 minutes with automatic refetch on focus
 * - All mutations invalidate relevant queries to ensure data consistency
 * 
 * Authorization:
 * - Backend uses scoped queries (where recipient_id = current_user_id)
 */
import {useQueryClient, useMutation, useInfiniteQuery, useQuery} from '@tanstack/react-query';
import {
  fetchUserMessages,
  fetchUserMessageStatus,
  markMessageAsRead,
  markAllMessagesAsRead,
} from '../endpoints/api';

/**
 * Fetch user messages with infinite scroll pagination.
 * 
 * @param enabled - Whether to execute the query. Set to false for on-demand fetching.
 *                  When false, messages won't load until explicitly needed (e.g., modal opens).
 * 
 * Query Key: ['user', 'messages']
 * Cache Strategy: 2-minute stale time, refetches on window focus
 * Cache Invalidation: Triggered by useMarkMessageAsRead, useMarkAllMessagesAsRead
 * 
 * @returns InfiniteQuery with message pages and pagination controls
 */
export const useGetUserMessages = (enabled = true) => {
  return useInfiniteQuery(
    ['user', 'messages'],
    ({pageParam = 1}) => fetchUserMessages({page: String(pageParam)}),
    {
      enabled,
      staleTime: 1000 * 60 * 2, // 2 minutes - matches status endpoint
      refetchOnWindowFocus: true,
      getNextPageParam: (lastPage) => {
        // SimplePaginator: check if links.next exists
        const hasNextPage = lastPage?.data?.links?.next !== null;
        const currentPage = lastPage?.data?.meta?.current_page;
        
        if (hasNextPage && currentPage) {
          return currentPage + 1;
        }
        return undefined;
      },
    }
  );
};

/**
 * Fetch lightweight status of unread messages using optimized exists() query.
 * 
 * Performance: O(1) query - only checks existence, doesn't fetch message data.
 * Use this for UI indicators (badge, icon highlight) instead of full message fetch.
 * 
 * Query Key: ['user', 'messages', 'status']
 * Cache: 2-minute staleTime, refetches on window focus
 * Cache Invalidation: Triggered by useMarkMessageAsRead, useMarkAllMessagesAsRead
 * 
 * @returns Query with {has_unread: boolean}
 */
export const useGetUserMessageStatus = () => {
  return useQuery(['user', 'messages', 'status'], fetchUserMessageStatus, {
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
  });
};

/**
 * Mark a specific message as read.
 * 
 * Invalidates both messages and status queries on success to ensure UI consistency.
 * 
 * @returns Mutation function that accepts a message UUID
 */
export const useMarkMessageAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation((uuid: string) => markMessageAsRead(uuid), {
    onSuccess: () => {
      queryClient.invalidateQueries(['user', 'messages']);
      queryClient.invalidateQueries(['user', 'messages', 'status']);
    },
  });
};

/**
 * Mark all user messages as read.
 * 
 * Invalidates both messages and status queries on success to ensure UI consistency.
 * 
 * @returns Mutation function
 */
export const useMarkAllMessagesAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation(() => markAllMessagesAsRead(), {
    onSuccess: () => {
      queryClient.invalidateQueries(['user', 'messages']);
      queryClient.invalidateQueries(['user', 'messages', 'status']);
    },
  });
};
